import { getConversationRecoveryEnrichment } from '../../api/conversation-recovery-query.util';
import { getCallSnippet } from './call-preview.util';
import {
  affectedConversationQueries,
  conversationNewQueryMatcher,
} from './query-impact.util';
import { recentConversationMutationScope } from './mutation-impact.util';
import {
  getAcceptedConversation,
  rememberConversationEvent,
  projectKnownConversationRows,
} from './recent-conversation-events.util';
import type {
  ConversationDocumentPatch,
  ApplyConversationDocumentPatchParams,
  ApplyRealtimeFunnelConversationUpdateParams,
} from '../../../types/cache/funnel-conversations/realtime-update.types';
import type { QueryClient } from '@tanstack/react-query';
import {
  type Call_V2,
  type FunnelConversation,
  type FunnelConversationDocument,
  FunnelInteractionDirection,
  FunnelInteractionType,
  type Message,
  MessageDirection,
} from 'src/api/generated';
import { isSortedByLatestInteraction } from 'src/modules/shared/communications/utils/cache/query-matcher.util';
import { recoverConversationDocuments } from 'src/modules/shared/communications/services/conversation-document-recovery.service';
import { refreshConversationSearch } from 'src/modules/shared/communications/services/conversation-search-refresh.service';
import {
  type InfiniteQueryData,
  isInfiniteQueryData,
  isRegularQueryData,
  type RegularQueryData,
  updateCache,
} from 'src/modules/shared/utils/cache/update.util';
import {
  updateFunnelConversationCache,
  updateFunnelConversationResourceCache,
} from './update.util';

const getFunnelInteractionDirection = (
  direction: string | undefined
): FunnelInteractionDirection =>
  direction === MessageDirection.OUTBOUND || direction === 'outgoing'
    ? FunnelInteractionDirection.OUTGOING
    : FunnelInteractionDirection.INCOMING;

const getMessageSnippet = (message: Message): string => {
  const content = message.content.trim();
  if (content) {
    return content;
  }

  if ((message.attachments?.length ?? 0) > 0) {
    return 'Adjunto';
  }

  return 'Mensaje';
};

const isConversationDocument = (
  data: unknown
): data is FunnelConversationDocument => {
  return typeof data === 'object' && data !== null && 'id' in data;
};

const shouldApplyLastInteractionPatch = (
  document: FunnelConversationDocument,
  patch: ConversationDocumentPatch
): boolean => {
  if (!patch.lastInteractionAt || !patch.lastInteraction) {
    return true;
  }

  if (!document.lastInteractionAt || !document.lastInteraction) {
    return true;
  }

  const isSameInteraction =
    document.lastInteraction.id === patch.lastInteraction.id &&
    document.lastInteraction.modelType === patch.lastInteraction.modelType;
  if (isSameInteraction) {
    return true;
  }

  const patchTimestamp = Date.parse(patch.lastInteractionAt);
  const documentTimestamp = Date.parse(document.lastInteractionAt);
  if (Number.isNaN(patchTimestamp) || Number.isNaN(documentTimestamp)) {
    return true;
  }

  return patchTimestamp >= documentTimestamp;
};

const getSafeLastInteractionPatch = (
  document: FunnelConversationDocument,
  patch: ConversationDocumentPatch
): ConversationDocumentPatch => {
  if (shouldApplyLastInteractionPatch(document, patch)) {
    return patch;
  }

  const {
    lastInteraction: _lastInteraction,
    lastInteractionAt: _lastInteractionAt,
    ...safePatch
  } = patch;

  return safePatch;
};

const updateConversationDocument = (
  document: FunnelConversationDocument,
  funnelConversationId: number,
  patch: ConversationDocumentPatch
): FunnelConversationDocument => {
  if (document.id.toString() !== funnelConversationId.toString()) {
    return document;
  }

  const lastInteractionPatch = getSafeLastInteractionPatch(document, patch);

  return {
    ...document,
    ...lastInteractionPatch,
    updatedAt:
      Date.parse(patch.updatedAt ?? '') > Date.parse(document.updatedAt)
        ? patch.updatedAt!
        : document.updatedAt,
  };
};

const sortConversationDocuments = (
  items: FunnelConversationDocument[]
): FunnelConversationDocument[] =>
  [...items].sort(
    (left, right) =>
      (Date.parse(right.lastInteractionAt ?? '') || 0) -
        (Date.parse(left.lastInteractionAt ?? '') || 0) ||
      left.id.localeCompare(right.id)
  );

const removeFunnelConversationCaches = (
  queryClient: QueryClient,
  funnelConversationId: number,
  removeDocuments = true
): void => {
  if (removeDocuments) {
    updateCache({
      queryClient,
      queryKey: ['crm/documents/funnel-conversations'],
      updatedData: {
        id: funnelConversationId.toString(),
      } as FunnelConversationDocument,
      shouldAddItem: () => false,
      shouldRemoveItem: (_matchedKey, oldItem) =>
        oldItem.id.toString() === funnelConversationId.toString(),
    });
  }

  updateCache({
    queryClient,
    queryKey: ['crm/funnel-conversations'],
    updatedData: {
      id: funnelConversationId,
    } as FunnelConversation,
    shouldAddItem: () => false,
    shouldRemoveItem: (_matchedKey, oldItem) =>
      oldItem.id.toString() === funnelConversationId.toString(),
  });

  queryClient.removeQueries({
    queryKey: ['crm/funnel-conversations', funnelConversationId],
  });
};

/**
 * Builds a conversation-document patch for a message interaction.
 *
 * @param message - Message snapshot received from realtime.
 * @returns Conversation document patch for last-interaction fields.
 */
export const getMessageConversationPatch = (
  message: Message
): ConversationDocumentPatch => ({
  lastInteractionAt: message.createdAt,
  lastInteraction: {
    id: message.id,
    modelType: FunnelInteractionType.MESSAGE,
    direction: getFunnelInteractionDirection(message.direction),
    status: message.status,
    snippet: getMessageSnippet(message),
  },
  updatedAt: message.updatedAt,
});

/**
 * Builds a conversation-document patch for a call interaction.
 *
 * @param call - Call snapshot received from realtime.
 * @returns Conversation document patch for last-interaction fields.
 */
export const getCallConversationPatch = (
  call: Call_V2
): ConversationDocumentPatch => ({
  lastInteractionAt: call.startTime,
  lastInteraction: {
    id: call.id.toString(),
    modelType: FunnelInteractionType.CALL,
    direction: getFunnelInteractionDirection(call.direction),
    status: call.status,
    snippet: getCallSnippet(call),
  },
  updatedAt: call.updatedAt,
});

/**
 * Applies an interaction patch to funnel conversation document caches.
 *
 * @param params - Cache patch inputs including the target conversation id.
 */
export const applyConversationDocumentPatch = ({
  queryClient,
  funnelConversationId,
  action,
  patch,
}: ApplyConversationDocumentPatchParams): void => {
  if (action === 'deleted') {
    return;
  }

  const matchedQueries = queryClient.getQueriesData<
    | InfiniteQueryData<FunnelConversationDocument>
    | RegularQueryData<FunnelConversationDocument>
    | FunnelConversationDocument
  >({
    queryKey: ['crm/documents/funnel-conversations'],
    exact: false,
  });

  matchedQueries.forEach(([matchedKey, oldData]) => {
    if (!oldData) {
      return;
    }

    if (isInfiniteQueryData<FunnelConversationDocument>(oldData)) {
      let pages = oldData.pages.map((page) => ({
        ...page,
        items: page.items.map((item) =>
          updateConversationDocument(item, funnelConversationId, patch)
        ),
      }));
      if (isSortedByLatestInteraction(matchedKey)) {
        const sorted = sortConversationDocuments(
          pages.flatMap((page) => page.items)
        );
        let offset = 0;
        pages = pages.map((page) => {
          const items = sorted.slice(offset, offset + page.items.length);
          offset += page.items.length;
          return { ...page, items };
        });
      }
      queryClient.setQueryData(matchedKey, { ...oldData, pages });
      return;
    }

    if (isRegularQueryData<FunnelConversationDocument>(oldData)) {
      const items = oldData.data.map((item) =>
        updateConversationDocument(item, funnelConversationId, patch)
      );
      queryClient.setQueryData(matchedKey, {
        ...oldData,
        data: isSortedByLatestInteraction(matchedKey)
          ? sortConversationDocuments(items)
          : items,
      });
      return;
    }

    if (isConversationDocument(oldData)) {
      queryClient.setQueryData(
        matchedKey,
        updateConversationDocument(oldData, funnelConversationId, patch)
      );
    }
  });
};

const previousConversation = (
  queryClient: QueryClient,
  funnelConversationId: number,
  tenantId?: string
): FunnelConversation | undefined => {
  const cached = queryClient.getQueryData<FunnelConversation>([
    'crm/funnel-conversations',
    funnelConversationId,
  ]);
  return tenantId
    ? getAcceptedConversation(queryClient, tenantId, funnelConversationId) ??
        (cached?.tenantId === tenantId ? cached : undefined)
    : undefined;
};

/**
 * Applies a realtime conversation snapshot to funnel conversation caches.
 *
 * @param params - Cache update inputs including optional snapshots.
 */
export const applyRealtimeFunnelConversationUpdate = ({
  queryClient,
  funnelConversationId,
  action,
  conversation,
  document,
  tenantId = conversation?.tenantId ?? document?.tenantId,
  occurredAt = conversation?.updatedAt,
}: ApplyRealtimeFunnelConversationUpdateParams): void => {
  const previous = previousConversation(
    queryClient,
    funnelConversationId,
    tenantId
  );
  const source = conversation ?? previous;
  const protectedProjection =
    tenantId && occurredAt
      ? rememberConversationEvent(
          queryClient,
          funnelConversationId,
          tenantId,
          occurredAt,
          conversation,
          action === 'deleted'
        )
      : false;
  const scope = source
    ? new Set([
        ...affectedConversationQueries(
          queryClient,
          source,
          previous,
          action === 'deleted',
          !protectedProjection
        ),
        ...recentConversationMutationScope(
          queryClient,
          source.id,
          source.tenantId
        ),
      ])
    : undefined;
  // Carry the existing obligation before the newer snapshot cancels its read.
  const enrichment =
    action === 'updated' && tenantId
      ? getConversationRecoveryEnrichment(
          queryClient,
          funnelConversationId,
          tenantId
        )
      : undefined;
  if (source && scope && enrichment) {
    affectedConversationQueries(
      queryClient,
      source,
      previous,
      false,
      true
    ).forEach((query) => {
      if (enrichment.scope.has(query)) scope.add(query);
    });
  }
  // Reject completion of a detail read started before this accepted event.
  // Reverting cancellation would restore the pre-event snapshot instead.
  void queryClient.cancelQueries(
    {
      queryKey: ['crm/funnel-conversations', funnelConversationId],
    },
    { revert: false }
  );
  if (action === 'deleted') {
    removeFunnelConversationCaches(
      queryClient,
      funnelConversationId,
      !!protectedProjection
    );
    void refreshConversationSearch(
      queryClient,
      scope,
      source ? conversationNewQueryMatcher(source, previous, true) : undefined
    );
    return;
  }

  const context = {
    params: { id: funnelConversationId, data: conversation ?? {} },
  };
  if (document) {
    updateFunnelConversationCache(queryClient, context, conversation, document);
    return;
  }
  if (!conversation) return;
  // Canonical scalar fields can update known rows; search retains enrichment,
  // unseen membership, exact totals and cursor pagination ownership.
  updateFunnelConversationResourceCache(queryClient, context, conversation);
  queryClient.setQueryData(
    ['crm/funnel-conversations', funnelConversationId],
    conversation
  );
  if (protectedProjection) {
    projectKnownConversationRows(queryClient, conversation);
  }
  if (!protectedProjection || scope?.size) {
    const matchesNewQuery = conversationNewQueryMatcher(conversation, previous);
    void refreshConversationSearch(queryClient, scope, matchesNewQuery);
    void recoverConversationDocuments(
      queryClient,
      conversation,
      scope,
      matchesNewQuery
    );
  }
};
